// ============================================================
// /v2 report API — response types and pure mapping helpers.
//
// Shapes mirror packages/shared/src/analytics/query/reports.ts and
// packages/api/src/routes/v2/{reports,manage}.ts. v2 responses are
// kept snake_case on purpose (no global camelCase transform): keys such
// as counter aliases and `d:<name>` dimensions are data, not field names.
// Everything here is side-effect free so it can be unit tested.
// ============================================================

import {
    GA4_DEFAULT_EVENT_MAP,
    META_DEFAULT_EVENT_MAP,
    exactSince,
    zonedMidnightMs,
    type ProjectAnalyticsConfig,
} from '@yourownmetric/shared'

import type { AdDestinationConfig } from './opsTypes'

// ---------- request params ----------

export interface V2RangeArgs {
    projectId: string
    from: string
    to: string
    /** null = whole project, '' = the project's own traffic, else a sub-tenant external id */
    subTenant?: string | null
}

type ParamValue = string | number | boolean | null | undefined

/** Query string for a report: from/to/st plus report-specific params (empty values dropped). */
export function reportQuery(args: V2RangeArgs, extra: Record<string, ParamValue> = {}): string {
    const q = new URLSearchParams()
    q.set('from', args.from)
    q.set('to', args.to)
    if (args.subTenant !== undefined && args.subTenant !== null) {
        // The API reads `st=-` as "no sub-tenant" (the project's own events).
        q.set('st', args.subTenant === '' ? '-' : args.subTenant)
    }
    for (const [k, v] of Object.entries(extra)) {
        if (v !== undefined && v !== null && v !== '') {
            q.set(k, String(v))
        }
    }
    return q.toString()
}

/**
 * v2 lives next to v1 on the same host: derive its base from VITE_API_URL
 * (`https://api.example.com/v1` → `https://api.example.com/v2`). The dev
 * default `/api` is a Vite proxy to /v1, so v2 gets its own `/v2` proxy.
 */
export function deriveV2BaseUrl(v1Base: string | undefined): string {
    const base = (v1Base ?? '/api').replace(/\/+$/, '')
    if (/\/v1$/.test(base)) {
        return base.replace(/\/v1$/, '/v2')
    }
    if (base === '/api' || base === '') {
        return '/v2'
    }
    return `${base}/v2`
}

// ---------- envelopes & errors ----------

export interface V2Envelope<T> {
    success: boolean
    data: T
    error?: { code: string; message: string }
}

export function unwrap<T>(res: V2Envelope<T>): T {
    return res.data
}

/** Human message from an RTK Query error for a v2 call. */
export function v2ErrorMessage(err: unknown): string | null {
    if (!err) {
        return null
    }
    const e = err as { status?: unknown; data?: unknown; error?: string }
    let data = e.data as { error?: { message?: string; code?: string } } | undefined
    if (typeof e.data === 'string') {
        // text responses (e.g. /export) carry the JSON error envelope as a string
        try {
            data = JSON.parse(e.data) as typeof data
        } catch {
            data = undefined
        }
    }
    if (data?.error?.message) {
        return data.error.message
    }
    if (typeof e.error === 'string') {
        return e.error
    }
    if (e.status !== undefined) {
        return `Request failed (${String(e.status)})`
    }
    return 'Request failed'
}

// ---------- overview ----------

export interface OverviewKpis {
    events: number | null
    pageviews: number | null
    sessions: number | null
    visitors: number | null
    engaged_sessions: number | null
    bounce_rate: number | null
    avg_session_ms: number | null
    pages_per_session: number | null
    purchases: number
    revenue: number | null
    refunds: number | null
    aov: number | null
    conversion_rate: number | null
    leads: number | null
    errors: number | null
    avg_engaged_ms: number | null
}

export interface OverviewResponse {
    range: { from: string; to: string; tz: string }
    source: 'raw' | 'rollup'
    /** Sampled store / HyperLogLog distincts: the numbers are estimates. */
    estimate?: boolean
    kpis: OverviewKpis
    previous_range?: { from: string; to: string }
    previous?: OverviewKpis
}

export interface Overview extends OverviewResponse {
    /** Percent change vs the previous period per KPI (null when not comparable). */
    change: Partial<Record<keyof OverviewKpis, number | null>>
}

export function pctChange(cur: number | null | undefined, prev: number | null | undefined) {
    if (cur === null || cur === undefined || prev === null || prev === undefined || prev === 0) {
        return null
    }
    return Math.round(((cur - prev) / Math.abs(prev)) * 1000) / 10
}

export function mapOverview(res: V2Envelope<OverviewResponse>): Overview {
    const d = res.data
    const change: Overview['change'] = {}
    if (d.previous) {
        for (const k of Object.keys(d.kpis) as Array<keyof OverviewKpis>) {
            change[k] = pctChange(d.kpis[k], d.previous[k])
        }
    }
    return { ...d, change }
}

// ---------- timeseries ----------

export interface TimeseriesResponse {
    metric: string
    interval: string
    points: Array<{ t: string | null; value: number | null }>
}

export interface SeriesPoint {
    t: string
    value: number
}

export function mapTimeseries(res: V2Envelope<TimeseriesResponse>): SeriesPoint[] {
    return res.data.points
        .filter(p => p.t)
        .map(p => ({ t: String(p.t), value: Number(p.value ?? 0) }))
}

/** Merge several series on their bucket key: [{ t, a, b }], sorted by t; missing buckets = 0. */
export function mergeSeries(
    series: Record<string, SeriesPoint[] | undefined>,
): Array<Record<string, number | string>> {
    const names = Object.keys(series)
    const byT = new Map<string, Record<string, number | string>>()
    for (const name of names) {
        for (const p of series[name] ?? []) {
            const row = byT.get(p.t) ?? { t: p.t }
            row[name] = p.value
            byT.set(p.t, row)
        }
    }
    return [...byT.values()]
        .map(r => {
            for (const name of names) {
                r[name] ??= 0
            }
            return r
        })
        .sort((a, b) => String(a.t).localeCompare(String(b.t)))
}

/**
 * Daily series only list days with data: add the missing days of [from, to] as zeros
 * so the chart's x-axis is the whole range. Other intervals are returned unchanged.
 */
export function fillDailyGaps(
    rows: Array<Record<string, number | string>>,
    interval: string,
    from: string,
    to: string,
    keys: string[],
): Array<Record<string, number | string>> {
    if (interval !== 'day' || rows.length === 0 || from > to) {
        return rows
    }
    const byDay = new Map(rows.map(r => [String(r.t).slice(0, 10), r]))
    const out: Array<Record<string, number | string>> = []
    const d = new Date(`${from}T00:00:00Z`)
    for (let i = 0; i < 400; i++) {
        const day = d.toISOString().slice(0, 10)
        if (day > to) {
            break
        }
        out.push(byDay.get(day) ?? { t: day, ...Object.fromEntries(keys.map(k => [k, 0])) })
        d.setUTCDate(d.getUTCDate() + 1)
    }
    return out
}

/** Short axis label for a bucket key ('2026-10-21', '2026-10-21 13:00:00', ISO…). */
export function bucketLabel(t: string, interval: string): string {
    const m = /^(\d{4})-(\d{2})-(\d{2})(?:[ T](\d{2}):(\d{2}))?/.exec(t)
    if (!m) {
        return t
    }
    if (interval === 'hour' && m[4]) {
        return `${m[4]}:${m[5]}`
    }
    if (interval === 'month') {
        return `${m[1]}-${m[2]}`
    }
    return `${Number(m[2])}/${Number(m[3])}`
}

// ---------- breakdown / events ----------

export interface BreakdownRow {
    key: string
    count: number
    sessions: number
    visitors: number
    value: number
    share: number | null
}

export interface BreakdownResponse {
    dim: string
    event: string | null
    rows: Array<Record<string, string | number | null>>
}

const num = (v: unknown): number => {
    const x = typeof v === 'number' ? v : Number(v)
    return Number.isFinite(x) ? x : 0
}

function toBreakdownRow(r: Record<string, string | number | null>): BreakdownRow {
    return {
        key: r.key === null || r.key === undefined ? '(none)' : String(r.key),
        count: num(r.count),
        sessions: num(r.sessions),
        visitors: num(r.visitors),
        value: num(r.value),
        share: r.share === null || r.share === undefined ? null : num(r.share),
    }
}

/**
 * Breakdown rows. With an `event`, the API still groups keys seen on any event and
 * returns 0 for keys without that event — those rows are dropped.
 */
export function mapBreakdown(res: V2Envelope<BreakdownResponse>): BreakdownRow[] {
    return res.data.rows.map(toBreakdownRow).filter(r => r.count > 0)
}

export function mapEventsReport(
    res: V2Envelope<{ events: Array<Record<string, string | number | null>> }>,
): BreakdownRow[] {
    const rows = res.data.events.map(toBreakdownRow)
    const total = rows.reduce((s, r) => s + r.count, 0)
    return rows.map(r => ({
        ...r,
        share: total > 0 ? Math.round((r.count / total) * 10_000) / 10_000 : null,
    }))
}

// ---------- realtime ----------

export interface RealtimeReport {
    minutes: number
    events: number
    active_sessions: number
    active_visitors: number
    per_minute: Array<{ t: string; events: number }>
    top_pages: Array<{ path: string; sessions: number }>
}

// ---------- raw events (export ndjson) ----------

export interface RawEvent {
    time: string
    event: string
    path: string
    channel: string
    referrer: string
    country: string
    device: string
    sub_tenant: string
    label: string
    item_id: string
    value: number
    session: string
    [key: string]: unknown
}

/** Parse the /export?format=ndjson body (one JSON object per line, newest first). */
export function parseNdjson(text: string): RawEvent[] {
    const out: RawEvent[] = []
    for (const line of text.split('\n')) {
        const s = line.trim()
        if (!s) {
            continue
        }
        try {
            const r = JSON.parse(s) as Record<string, unknown>
            const t = r.time
            out.push({
                ...r,
                time:
                    typeof t === 'number'
                        ? new Date(t).toISOString()
                        : typeof t === 'string'
                          ? t
                          : '',
                event: String(r.event ?? ''),
                path: String(r.path ?? ''),
                channel: String(r.channel ?? ''),
                referrer: String(r.referrer ?? ''),
                country: String(r.country ?? ''),
                device: String(r.device ?? ''),
                sub_tenant: String(r.sub_tenant ?? ''),
                label: String(r.label ?? ''),
                item_id: String(r.item_id ?? ''),
                value: num(r.value),
                session: String(r.session ?? ''),
            })
        } catch {
            // skip a partial line
        }
    }
    return out
}

// ---------- ecommerce ----------

export interface EcommerceReport {
    funnel: Array<{
        step: string
        events: number | null
        sessions: number | null
        rate_from_previous: number | null
        rate_from_start: number | null
    }>
    revenue: number | null
    refunds: number | null
    purchases: number
    aov: number | null
    items_sold: number | null
    cart_value: number | null
    conversion_rate: number | null
    items: Array<{
        item_id: string
        views: number | null
        add_to_carts: number | null
        purchased_qty: number | null
        revenue: number | null
        cart_rate: number | null
    }>
}

/**
 * Events whose counts are exact (unsampled) for a range starting `from`:
 * built-in conversion events always, custom events flagged `exact` since
 * the flag was saved (exactSince). Mirrors how the API picks the exact store.
 */
export function exactEventsForRange(
    config: ProjectAnalyticsConfig | undefined,
    events: readonly string[],
    from: string,
): Set<string> {
    const out = new Set<string>()
    if (!config) {
        return out
    }
    const start = zonedMidnightMs(from, config.timezone || 'UTC')
    for (const e of events) {
        const since = exactSince(e, config)
        if (since !== null && since <= start) {
            out.add(e)
        }
    }
    return out
}

// ---------- goals / funnels ----------

export interface EventMatcher {
    event: string
    label?: string
    item_id?: string
    path_prefix?: string
    server?: boolean
}

export interface GoalDef {
    id: string
    name: string
    match: EventMatcher
    created_at?: string
}

export interface GoalsReport {
    sessions?: number | null
    goals: Array<{
        id: string
        name: string
        conversions: number | null
        converting_sessions: number | null
        converting_visitors: number | null
        value: number | null
        conversion_rate: number | null
    }>
}

export interface FunnelDef {
    id: string
    name: string
    steps: EventMatcher[]
    window_days: number
    by: 'visitor' | 'session'
    created_at?: string
}

export interface FunnelReport {
    id?: string
    name?: string
    by: 'visitor' | 'session'
    window_ms: number
    /** true: every step from the unsampled exact store; false: a sampling-based estimate. */
    exact?: boolean
    steps: Array<{
        step: number
        match: EventMatcher
        count: number
        rate_from_previous: number | null
        rate_from_start: number | null
    }>
}

/** Matcher from builder inputs: trims, drops empty optional fields. */
export function cleanMatcher(m: {
    event: string
    label?: string
    item_id?: string
    path_prefix?: string
}): EventMatcher {
    const out: EventMatcher = { event: m.event.trim() }
    for (const k of ['label', 'item_id', 'path_prefix'] as const) {
        const v = m[k]?.trim()
        if (v) {
            out[k] = v
        }
    }
    return out
}

export function matcherLabel(m: EventMatcher): string {
    const parts = [m.event]
    if (m.path_prefix) {
        parts.push(`path ${m.path_prefix}*`)
    }
    if (m.label) {
        parts.push(`label=${m.label}`)
    }
    if (m.item_id) {
        parts.push(`item=${m.item_id}`)
    }
    return parts.join(' · ')
}

// ---------- attribution / retention / performance / errors ----------

export interface AttributionReport {
    model: string
    window_days: number
    conversions: number
    value: number
    attributed_conversions: number
    attributed_value: number
    attributed_share: number | null
    by_channel: Array<{ key: string; conversions: number; value: number }>
    by_touch: Array<{ key: string; conversions: number; value: number }>
    /** Conversion count / value are exact (unsampled). */
    exact_conversions?: boolean
    /** The whole report is exact; false = touches and attributed numbers are estimates. */
    exact?: boolean
    truncated: boolean
}

export interface RetentionReport {
    interval: 'day' | 'week'
    periods: number
    truncated: boolean
    cohorts: Array<{
        cohort: string
        size: number
        retained: number[]
        rates: Array<number | null>
    }>
}

export type VitalRating = 'good' | 'needs_improvement' | 'poor' | null

export interface PerformanceReport {
    metrics: Array<{
        metric: string
        samples: number
        p75: number
        rating: VitalRating
        by_device: Array<{
            device: string
            samples: number
            p50: number
            p75: number
            p95: number
            rating: VitalRating
        }>
    }>
}

export interface ErrorsReport {
    errors: Array<{
        error_type: string
        fingerprint: string
        count: number
        sessions: number
        first_seen: string
        last_seen: string
    }>
}

// ---------- usage ----------

export interface UsageReport {
    by: 'sub_tenant'
    rows: Array<{ key: string; events: number | null; sessions: number | null }>
}

export interface UsageSummary {
    total: number
    rows: Array<{ key: string; label: string; events: number; sessions: number; share: number }>
}

export function mapUsage(res: V2Envelope<UsageReport>): UsageSummary {
    const rows = res.data.rows.map(r => ({
        key: r.key,
        label: r.key === '' ? '(project itself)' : r.key,
        events: num(r.events),
        sessions: num(r.sessions),
    }))
    const total = rows.reduce((s, r) => s + r.events, 0)
    return {
        total,
        rows: rows
            .map(r => ({ ...r, share: total > 0 ? r.events / total : 0 }))
            .sort((a, b) => b.events - a.events),
    }
}

/** Plan cap meter: share used (0…1+, null without a cap). */
export function capUsage(used: number | undefined, limit: number | undefined) {
    if (!limit || limit <= 0) {
        return null
    }
    return (used ?? 0) / limit
}

// ---------- management ----------

export interface SubTenant {
    id: string
    external_id: string
    name: string
    status: 'active' | 'suspended' | string
    metadata: Record<string, unknown>
    created_at: string
    updated_at: string
}

export interface ProjectConfigResponse {
    config: ProjectAnalyticsConfig
    entitlements: {
        maxSubTenants: number | null
        maxDestinationsPerProject: number
        realtimeDestinations: boolean
        embedTokens: boolean
        maxRetentionDays: number
        exportWindowDays: number
        apiRequestsPerMinute: number
        // Agency portals + scheduled reports (optional: older APIs omit them;
        // unlimited values arrive as null because JSON has no Infinity).
        maxPortals?: number | null
        maxPortalUsers?: number | null
        portalCustomDomain?: boolean
        removeBranding?: boolean
        maxScheduledReports?: number | null
        maxReportRecipients?: number | null
        reportCsv?: boolean
    }
}

export interface Destination {
    id: string
    type: string
    name: string
    url: string
    sub_tenant: string | null
    events: string[]
    mode: 'realtime' | 'batched'
    active: boolean
    disabled_reason: string | null
    created_at: string
    updated_at: string
    /** Only on create / rotate — shown once. */
    secret?: string
    /** Ad-platform destinations: non-secret config (ids, effective event map). */
    config?: AdDestinationConfig
    /** Ad-platform destinations: the credential is write-only, GET shows a masked hint. */
    credential?: { set: boolean; hint: string }
}

export const DESTINATION_TYPES = [
    { value: 'webhook', label: 'Signed webhook' },
    { value: 'meta_capi', label: 'Meta Conversions API' },
    { value: 'ga4_mp', label: 'GA4 Measurement Protocol' },
] as const
export type DestinationType = (typeof DESTINATION_TYPES)[number]['value']

/** POST /destinations body. Webhooks carry url + events; ad platforms carry ids + credential. */
export type DestinationInput = {
    type: DestinationType
    name: string
    mode: 'realtime' | 'batched'
    sub_tenant: string | null
    url?: string
    events?: string[]
    pixel_id?: string
    access_token?: string
    test_event_code?: string
    measurement_id?: string
    api_secret?: string
}

export interface DestinationForm {
    type: DestinationType
    name: string
    url: string
    events: string
    mode: 'realtime' | 'batched'
    subTenant: string
    pixelId: string
    accessToken: string
    testEventCode: string
    measurementId: string
    apiSecret: string
}

export const EMPTY_DESTINATION_FORM: DestinationForm = {
    type: 'webhook',
    name: '',
    url: '',
    events: '*',
    mode: 'batched',
    subTenant: '',
    pixelId: '',
    accessToken: '',
    testEventCode: '',
    measurementId: '',
    apiSecret: '',
}

/** The create body for a form: only the fields that apply to its type (the API rejects others). */
export function destinationInput(f: DestinationForm): DestinationInput {
    const common = {
        type: f.type,
        name: f.name.trim(),
        mode: f.mode,
        sub_tenant: f.subTenant.trim() || null,
    }
    if (f.type === 'meta_capi') {
        return {
            ...common,
            pixel_id: f.pixelId.trim(),
            access_token: f.accessToken.trim(),
            ...(f.testEventCode.trim() ? { test_event_code: f.testEventCode.trim() } : {}),
        }
    }
    if (f.type === 'ga4_mp') {
        return {
            ...common,
            measurement_id: f.measurementId.trim().toUpperCase(),
            api_secret: f.apiSecret.trim(),
        }
    }
    return { ...common, url: f.url.trim(), events: parseEventFilters(f.events) }
}

/** Whether the create button can be enabled (required fields per type). */
export function destinationFormReady(f: DestinationForm): boolean {
    if (f.type === 'meta_capi') {
        return !!f.pixelId.trim() && !!f.accessToken.trim()
    }
    if (f.type === 'ga4_mp') {
        return !!f.measurementId.trim() && !!f.apiSecret.trim()
    }
    return !!f.url.trim()
}

// --- Editing an ad-platform destination (PATCH /destinations/:id) ---

export type AdDestinationKind = 'meta_capi' | 'ga4_mp'

export function isAdDestination(d: Pick<Destination, 'type'>): d is Destination & {
    type: AdDestinationKind
} {
    return d.type === 'meta_capi' || d.type === 'ga4_mp'
}

/** The mappings every ad destination sends; `event_map` only adds or renames on top. */
export function defaultEventMap(type: AdDestinationKind): Readonly<Record<string, string>> {
    return type === 'meta_capi' ? META_DEFAULT_EVENT_MAP : GA4_DEFAULT_EVENT_MAP
}

export interface AdEditForm {
    name: string
    mode: 'realtime' | 'batched'
    subTenant: string
    pixelId: string
    measurementId: string
    /** New access token / API secret; empty keeps the stored one. */
    credential: string
    testEventCode: string
    /** Overrides on top of the defaults, one `yom_event = PlatformEvent` per line. */
    eventMap: string
}

/** GET returns the effective map (defaults + overrides); the form edits only the overrides. */
function eventMapOverrides(d: Destination & { type: AdDestinationKind }): Record<string, string> {
    const defaults = defaultEventMap(d.type)
    return Object.fromEntries(
        Object.entries(d.config?.event_map ?? {}).filter(([k, v]) => defaults[k] !== v),
    )
}

export function formatEventMap(m: Record<string, string>): string {
    return Object.entries(m)
        .map(([k, v]) => `${k} = ${v}`)
        .join('\n')
}

/** Parses `yom_event = PlatformEvent` lines (also `:` or `→`); blank lines are ignored. */
export function parseEventMapText(
    text: string,
): { ok: true; map: Record<string, string> } | { ok: false; error: string } {
    const map: Record<string, string> = {}
    const lines = text.split('\n')
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i].trim()
        if (!line) {
            continue
        }
        const m = /^([^=:→\s]+)\s*(?:=|:|→)\s*(\S+)$/.exec(line)
        if (!m) {
            return { ok: false, error: `Line ${i + 1}: write it as "yom_event = PlatformEvent"` }
        }
        map[m[1]] = m[2]
    }
    return { ok: true, map }
}

export function adEditForm(d: Destination & { type: AdDestinationKind }): AdEditForm {
    return {
        name: d.name,
        mode: d.mode,
        subTenant: d.sub_tenant ?? '',
        pixelId: d.config?.pixel_id ?? '',
        measurementId: d.config?.measurement_id ?? '',
        credential: '',
        testEventCode: d.config?.test_event_code ?? '',
        eventMap: formatEventMap(eventMapOverrides(d)),
    }
}

const sameMap = (a: Record<string, string>, b: Record<string, string>) =>
    JSON.stringify(Object.entries(a).sort()) === JSON.stringify(Object.entries(b).sort())

/**
 * The PATCH body for an edited ad destination: only what changed. The credential is sent only
 * when a new one was typed (it is write-only); clearing the test event code sends `null`.
 */
export function adDestinationPatch(
    d: Destination & { type: AdDestinationKind },
    f: AdEditForm,
): { ok: true; body: Record<string, unknown> } | { ok: false; error: string } {
    const parsed = parseEventMapText(f.eventMap)
    if (!parsed.ok) {
        return parsed
    }
    const body: Record<string, unknown> = {}
    const name = f.name.trim()
    if (name !== d.name) {
        body.name = name
    }
    if (f.mode !== d.mode) {
        body.mode = f.mode
    }
    const st = f.subTenant.trim() || null
    if (st !== d.sub_tenant) {
        body.sub_tenant = st
    }
    if (d.type === 'meta_capi') {
        const pixel = f.pixelId.trim()
        if (pixel !== (d.config?.pixel_id ?? '')) {
            body.pixel_id = pixel
        }
        if (f.credential.trim()) {
            body.access_token = f.credential.trim()
        }
        const code = f.testEventCode.trim()
        if (code !== (d.config?.test_event_code ?? '')) {
            body.test_event_code = code || null
        }
    } else {
        const mid = f.measurementId.trim().toUpperCase()
        if (mid !== (d.config?.measurement_id ?? '')) {
            body.measurement_id = mid
        }
        if (f.credential.trim()) {
            body.api_secret = f.credential.trim()
        }
    }
    if (!sameMap(parsed.map, eventMapOverrides(d))) {
        body.event_map = parsed.map
    }
    return { ok: true, body }
}

export interface DestinationTestResult {
    ok: boolean
    status: number | null
    duration_ms: number
    error?: string
    /** Meta: whether a test event went out (only with a test_event_code). */
    test_event_sent?: boolean
    /** GA4: messages from the Measurement Protocol validation server. */
    validation_messages?: Array<{ description?: string; fieldPath?: string } | string>
}

/** The notification line for a destination test. */
export function destinationTestMessage(r: DestinationTestResult): string {
    if (!r.ok) {
        return `Test failed: ${r.error ?? `HTTP ${r.status ?? '—'}`}`
    }
    const parts = [`Test delivered (${r.status ?? ''}, ${r.duration_ms} ms)`]
    if (r.test_event_sent) {
        parts.push('test event sent to Events Manager')
    }
    const msgs = (r.validation_messages ?? []).map(m =>
        typeof m === 'string' ? m : (m.description ?? m.fieldPath ?? ''),
    )
    if (msgs.length > 0) {
        parts.push(`GA4 validation: ${msgs.join('; ')}`)
    }
    return parts.join(' · ')
}

export interface Delivery {
    id: string
    payload_id: string
    event_type: string
    event_count: number
    kind: string
    attempt: number
    status: string
    http_status: number | null
    response_snippet: string | null
    error: string | null
    duration_ms: number | null
    manual: number
    created_at: number
    redeliverable: number
}

export interface DeliveriesResponse {
    deliveries: Delivery[]
    health: Record<string, unknown> | null
}

/** "*, event.purchase" → ['*', 'event.purchase'] */
export function parseEventFilters(raw: string): string[] {
    const list = raw
        .split(/[\s,]+/)
        .map(s => s.trim())
        .filter(Boolean)
    return list.length > 0 ? list : ['*']
}
